import { Attributes, Misc, Rules, Validation, ValueObject } from '@singularsystems/neo-core';
import { Display } from '@singularsystems/neo-core/dist/Model/Decorators';
import { AppService, DomainTypes } from '../../DomainTypes';
import { isMandatory, MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import IssuerInvestmentFieldConfig from '../Config/FieldConfig/IssuerInvestmentFieldConfig';
import { LiquidityLadder } from './LiquidityLadder';

export default class IssuerInvestmentDetail extends ValueObject {

    constructor() {
        super();
        this.makeObservable();
    }

    @Attributes.Nullable()
    public assetTypeId: number | null = null;

    @Attributes.Nullable()
    @Display("Profit Centre")
    public branchId: number | null = null;

    @Rules.StringLength(200)
    public security: string = "";

    @Attributes.Float()
    public fixedCostOfFunds: number = 0;

    @Rules.StringLength(50)
    public certificateNumber: string = "";

    @Rules.StringLength(50)
    public distinctiveNumber: string = "";

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public signingDate: Date | null = null;

    @Rules.StringLength(20)
    public ledgerCode: string = "";

    @Rules.StringLength(20)
    public businessUnitNo: string = "";

    @Attributes.Float()
    public liquidityPremium: number = 0;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public subscriptionDate: Date | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public migrationDate: Date | null = null;

    @Attributes.Nullable()
    public noticePeriodId: number | null = null;

    @Attributes.Float()
    public originalBalance: number = 0.0;

    @Display("PSD Asset")
    public psdAsset: boolean = false;

    public liquidityLadder: LiquidityLadder = LiquidityLadder.Include;

    @Display("NCC")
    @Attributes.Float()
    public ncc: number = 0;

    @Display("LP Determination Date")
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public lpDeterminationDate: Date | null = null;

    @Attributes.Float()
    public otherCost: number = 0.0;

    public equityUpside: boolean = false;

    public capacityWarehouse: boolean = false;

    @Display("GL Account Type")
    @Attributes.Nullable()
    public glAccountTypeId: number | null = null;

    @Display("SAP GL Code")
    @Rules.StringLength(20)
    public sapglCode: string = "";

    public qualifyingPurpose: boolean = false;
    
    @Attributes.Nullable()
    public productTypeId: number | null = null;

    @Rules.StringLength(100)
    public investmentName: string = "";

    @Rules.StringLength(14)
    public investmentCode: string = "";

    @Display("ISIN")
    @Rules.StringLength(16)
    public isin: string = "";

    @Attributes.Nullable()
    public distributionTypeId: number | null = null;

    // Client only properties / methods

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        const isDigit = (testString: string): boolean => !/[^0-9]/.test(testString);

        rules.failWhen(c => !isDigit(c.businessUnitNo), "Field can only contain numbers");
        rules.failWhen(c => c.liquidityPremium >= 1, "Liquidity Premium must be less than 100%");

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.assetTypeId && this.isRequired("assetTypeId"), "Asset type is required.");
        rules.failWhen(c => !c.certificateNumber && this.isRequired("certificateNumber"), "Certificate number is required.");
        rules.failWhen(c => !c.distinctiveNumber && this.isRequired("distinctiveNumber"), "Distinctive number is required.");
        rules.failWhen(c => !c.signingDate && this.isRequired("signingDate"), "Signing date is required.");
        rules.failWhen(c => !c.ledgerCode && this.isRequired("ledgerCode"), "Ledger code is required.");
        rules.failWhen(c => !c.businessUnitNo && this.isRequired("businessUnitNo"), "Field is required.");
        rules.failWhen(c => !c.subscriptionDate && this.isRequired("subscriptionDate"), "Subscription date is required.");
        rules.failWhen(c => !c.branchId && this.isRequired("branchId"), "Profit centre is required.");
        rules.failWhen(c => !c.security && this.isRequired("security"), "Security is required.");
        rules.failWhen(c => !c.noticePeriodId && this.isRequired("noticePeriodId"), "Notice period is required.");
        rules.failWhen(c => !c.lpDeterminationDate && this.isRequired("lpDeterminationDate"), "LP Determination Date is required.");
        rules.failWhen(c => !c.investmentName && this.isRequired("investmentName"), "Investment name is required.");
        rules.failWhen(c => !c.investmentCode && this.isRequired("investmentCode"), "Investment code is required.");
        rules.failWhen(c => !c.isin && this.isRequired("isin"), "ISIN is required.");
        rules.failWhen(c => !c.distributionTypeId && this.isRequired("distributionTypeId"), "Distribution Type is required.");
    }

    public toString(): string {
        return "Issuer Investment Info";
    }

    private isRequired(fieldName: keyof IssuerInvestmentFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.issuerInvestmentFields[fieldName] as MandatoryAndVisibleSetting);
    }
}